/**
 * DEX "Total fees" display reconciliation (issue #50). Pure — no React — so it's unit-testable
 * without a DOM render.
 *
 * The shown total must never read LESS than the sum of the fee line items above it. A same-chain
 * Jupiter swap quotes an all-in wedge (input − output, in USD) that can round BELOW the ~5-bps Parse
 * fee it still charges — so the raw total renders "0" while the Parse fee line shows a nonzero value
 * (e.g. "Total fees 0" over "Parse DeFi fee 0.000501 USDT"). DISPLAY-ONLY: the charged fee is
 * unchanged — we only clamp the SHOWN total up to the SHOWN components so the breakdown reconciles.
 */

/** The `dexFee` fields this computation reads (a structural subset of FeeSection's `dexFee` prop). */
export interface DexTotalInput {
  /** All-in fee, dest-token units (already formatted). */
  totalFee: string
  /** Parse platform fee, dest-token units (already formatted). */
  parseFee: string
  /** All-in fee in USD, when the backend computed one (cross-chain / native routes). */
  feesTotalUsd?: number
  /** Parse fee in USD (source-sized), when present. */
  parseFeeUsd?: number
  /** deBridge flat protocol fee in USD, when present. */
  fixFeeUsd?: number
  /** Generic rail cost in USD (e.g. 1Click), when present. */
  networkFeeUsd?: number
  /** Tron source-side energy burn in USD, when present. */
  tronEnergyFeeUsd?: number
}

/**
 * #261 F8 / MED-7 — THIS CONTRACT TAKES NO DISPLAY FLAG, deliberately.
 *
 * An earlier cut of F8 added `crossChainFeeDisplay?: boolean` here, which dropped `fixFeeUsd` from
 * the `shownUsd` sum whenever the flat fee's USD parenthetical was suppressed. Two things killed it:
 *
 *   1. It was INERT. For a deBridge payload `parseFeeUsd`/`networkFeeUsd` are not published
 *      (`dex.ts:911`/`:914` are 1Click-only), so `shownUsd` collapses to `fixFeeUsd` alone and
 *      dropping it changes `Math.max` only when `fixFeeUsd > feesTotalUsd` — the exact case the
 *      wedge branch excludes, since the wedge renders only when `feesTotalUsd − fixFeeUsd >= 0.005`.
 *   2. Its justification is gone. Operator decision E8 RESTORES the USD parenthetical on the flat-fee
 *      line (the wedge row is no longer called "Total fees", so a larger USD line item beside it is
 *      no longer a contradiction), so the flat fee is a SHOWN USD item again and must clamp.
 *
 * Keeping it out is also the strongest available form of F-C7: `dexFeeUnavailable`
 * (BridgePage.tsx:1788) feeds `canSend`, and with no flag to thread, the CTA gate and the two display
 * surfaces now run byte-identical arithmetic over the same payload by construction rather than by
 * discipline. Do not re-add a display parameter to this module.
 */

export interface DexDisplayTotals {
  /** Clamped USD total when the backend supplied an all-in USD figure; null in dest-token mode. */
  usd: number | null
  /** Clamped dest-token total string (reuses an already-formatted upstream string). */
  tokenStr: string
  /**
   * True when NO truthful positive figure exists — the total would render as a FALSE ZERO.
   * Two shapes: (1) dest-token mode where the fee falls under BridgePage's 6-dp format floor
   * (`fmt`, BridgePage.tsx:1653) so tokenStr is "0"; (2) USD mode where the figure would render
   * as "$0.00" at the 2-dp precision both surfaces print it at.
   * Consumers MUST render an explicit unavailable state — NEVER "0".
   *
   * USD THRESHOLD — `!(usd >= 0.005)`, i.e. "does not round up to at least one cent".
   * `usd === 0` was too narrow: the inputs carry MORE precision than the render does.
   * `feesTotalUsd` is 2-dp (dex.ts:863) but `parseFeeUsd`/`networkFeeUsd` are 4-dp (dex.ts:854,
   * :857) and `fixFeeUsd` 4-dp (dex.ts:1332), and the clamp below takes their MAX — so a
   * `max(0, 0.003)` survived as a nonzero `usd`, the flag stayed false, and the row printed
   * `≈ $0.00`. Reachable on near_1click with `nCollectedBps === 0` (dex.ts:795).
   *
   * ACCEPTED TRADE-OFF: a truthful sub-cent fee is now SUPPRESSED rather than shown. In a
   * high-rate fiat (e.g. IDR) $0.003 is a legible ~Rp50, so this state shows "Fee unavailable"
   * where a real figure existed. Less information, never a false one — the failure this row is
   * on the screen to prevent is a user reading "0" as "free" immediately before signing.
   *
   * WRITTEN NEGATED DELIBERATELY. `!(usd >= 0.005)` is fail-CLOSED on `NaN` (`NaN >= 0.005` is
   * false → flag true → "Fee unavailable"). The positive form `usd < 0.005` is fail-OPEN on NaN
   * (false → flag false → the row renders "$NaN"). Do not "simplify" the negation away.
   */
  belowDisplayFloor: boolean
}

function num(s: string): number {
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : 0
}

/**
 * The 2-dp render floor, in dollars. Both surfaces print every fiat fee figure via `toFixed(2)` or
 * `Intl` at 2 fraction digits, so anything under half a cent renders as a zero. ONE definition —
 * `belowDisplayFloor` below and every per-row guard in FeeSection read this same constant, because
 * four copies of a threshold is four places for it to drift.
 */
export const USD_DISPLAY_FLOOR = 0.005

/**
 * DEV-362 — the truthful upper bound for a SUPPRESSED (sub-floor) fee row. A per-row guard renders
 * this as `` `< ${fiatFee(USD_SUBCENT_BOUND)}` `` instead of the old 'Fee unavailable' for a fee that
 * is real but rounds to zero at the 2-dp render — replacing a false "data is missing" implication
 * with a true "the figure is smaller than this" one. `0.01` — ONE cent — is the smallest figure that
 * renders as a NON-zero cent at that same 2-dp precision, so "< $0.01" (or its converted-currency
 * equivalent via `fiatFee`) is a true statement about every value `usdRendersAsZero` suppresses,
 * including exactly `0`. Beside `USD_DISPLAY_FLOOR` on purpose — one definition, not a second copy of
 * the render precision for the two to drift out of step with each other.
 *
 * NEVER hardcode the literal string `'< $0.01'` at a call site: this bound must be passed through the
 * caller's OWN currency-aware formatter (`fiatFee` in FeeSection/index.tsx), exactly as every other
 * fiat figure on the card is. A hardcoded `$` literal stamps a US-dollar sign onto a card denominated
 * in the user's selected currency (e.g. IDR) — see the `formatFiatAmount` doc in
 * `hooks/useFiatPrices.ts:50-63`.
 *
 * QUALIFICATION (DEV-362 fixer pass, item 6): "never hardcode" is the right rule, but it is not true
 * that ONLY a hardcoded literal can produce a bare `$`. `fiatFee`'s own `?? '$'+usd.toFixed(2)`
 * fallback (FeeSection/index.tsx) stamps the same US-dollar sign onto the card whenever
 * `formatFiatAmount` returns `null` — which is `formatFiatAmount`'s documented behaviour whenever the
 * fiat rate is absent, and the rate is `null` BY DEFINITION for every USD user (`hooks/useFiatPrices.ts:
 * rate = isUsd ? null : …`). So a USD user sees a literal `$` today regardless of this rule — routing
 * the bound through `fiatFee` does not remove that `$`, it only guarantees non-USD users get their own
 * currency instead of a US-dollar sign that was never theirs. The directive here is about NOT adding a
 * SECOND, independent place a `$` can leak in on top of that existing, correct one.
 */
export const USD_SUBCENT_BOUND = 0.01

/**
 * Would this USD figure render as a zero? (#336 R6.)
 *
 * The rule this encodes: a fiat-rendered fee row must never print a figure that reads as zero. It is
 * the SAME question `belowDisplayFloor` asks of the clamped total — R1 gated the Parse row's
 * dest-token arm but left its USD arm unconditional and FIRST, so a `parseFeeUsd` of 0.003 still
 * printed `≈ $0.00` directly above `Total fees Fee unavailable`. `(0.003).toFixed(2) === '0.00'`.
 *
 * WRITTEN NEGATED DELIBERATELY, exactly as `belowDisplayFloor` is: `!(usd != null && usd >= FLOOR)`
 * is fail-CLOSED on NaN and on a missing value (`NaN >= 0.005` is false → true → 'Fee unavailable').
 * The positive form `usd < FLOOR` is fail-OPEN on NaN and would print `$NaN` on a fee row.
 */
export function usdRendersAsZero(usd: number | null | undefined): boolean {
  return !(usd != null && usd >= USD_DISPLAY_FLOOR)
}

/**
 * Is this USD figure genuinely MISSING — null, undefined, or non-finite (e.g. `NaN`, `Infinity`)?
 * (DEV-362.) Sibling of `usdRendersAsZero` above, extracted so a caller that needs only the "is there
 * a usable number here at all" question — as distinct from "does it render as zero" — has ONE
 * definition to call rather than re-deriving the null/NaN check inline at each call site. Same
 * one-definition doctrine as `USD_DISPLAY_FLOOR`: a second copy of this check is a second place for
 * it to drift.
 *
 * `fiatFeeRow` (FeeSection/index.tsx) is this predicate's reason for existing: its branch order is
 * "genuinely missing, THEN suppressed-total, THEN sub-cent, THEN normal" — and this is clause 1,
 * checked BEFORE the sub-cent branch. A missing value is not the same fact as a real value that is
 * merely too small to show, and conflating them (or checking sub-cent first) lets a `NaN` render a
 * false "< $0.01" instead of the honest "we don't have this figure".
 *
 * WRITTEN NEGATED DELIBERATELY, exactly as `usdRendersAsZero` is: `!(usd != null &&
 * Number.isFinite(usd))` is fail-CLOSED — a `NaN`, an `Infinity`, or an absent value all return
 * `true` ("missing"). The positive form (`usd == null || Number.isNaN(usd)`) reads the same on paper
 * but is TWO conditions a future edit can drift apart — and it also misses `Infinity`, which
 * `Number.isNaN` does not catch but `Number.isFinite` does. Keep this as ONE negated expression.
 */
export function usdIsMissing(usd: number | null | undefined): boolean {
  return !(usd != null && Number.isFinite(usd))
}

export function computeDexDisplayTotals(d: DexTotalInput): DexDisplayTotals {
  if (d.feesTotalUsd != null) {
    // USD mode: the total is never below the sum of the USD line items rendered above it.
    const shownUsd =
      (d.fixFeeUsd ?? 0)
      + (d.networkFeeUsd && d.networkFeeUsd > 0 ? d.networkFeeUsd : 0)
      + (d.tronEnergyFeeUsd && d.tronEnergyFeeUsd > 0 ? d.tronEnergyFeeUsd : 0)
      + (d.parseFeeUsd ?? 0)
    const usd = Math.max(d.feesTotalUsd, shownUsd)
    // Floor on what RENDERS (2 dp), not on `usd === 0` — see `belowDisplayFloor` above. The shared
    // predicate is the SAME one every per-row fiat guard in FeeSection uses (#336 R6), so the total
    // and its line items can never disagree about what "reads as zero" means. Negated form inside
    // `usdRendersAsZero` is load-bearing: it is fail-CLOSED on NaN.
    return { usd, tokenStr: d.totalFee, belowDisplayFloor: usdRendersAsZero(usd) }
  }
  // Dest-token mode (no USD all-in — e.g. same-chain Jupiter): the itemized fee is the dest-token
  // Parse fee, so the total must be at least that. Reuse the larger pre-formatted string.
  // NOTE the asymmetry with the USD arm above: `num(tokenStr) === 0` is correct HERE. `tokenStr` is
  // already the rendered string (BridgePage's `fmt` emitted it at 6 dp), so "is it zero" and "does
  // it render as zero" are the same question — there is no hidden precision to lose. `num` also
  // maps a non-finite parse to 0, so this arm is fail-CLOSED on garbage too.
  const tokenStr = num(d.parseFee) > num(d.totalFee) ? d.parseFee : d.totalFee
  return { usd: null, tokenStr, belowDisplayFloor: num(tokenStr) === 0 }
}

/**
 * Trim a full-precision token amount string (deBridge returns 18-dp) to ≤6 fraction digits.
 *
 * Lives HERE, not in the card, because #261 F8 puts the same native flat-fee amount on two surfaces
 * and the modal's own `formatDisplay` forces a 2-dp MINIMUM — it would render `1.00 ETH` where the
 * card renders `1 ETH`, i.e. the same fee stated two ways on two screens of one journey (ADDENDUM C
 * §7 / changelog 14). One formatter, imported by both — from HERE, directly, by each surface. (A
 * re-export through `FeeSection/index.tsx` was added and then removed at GATE 4: nothing imported it,
 * and its stated justification — "keeps the card's existing import surface unchanged" — was vacuous,
 * since `trimAmt` was module-private before this change and had no import surface to preserve.)
 */
export function trimAmt(value: string): string {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return value
  return n.toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: false })
}

/** The `dexFee` fields the #261 F8 display-mode decision reads. A structural subset, like `DexTotalInput`. */
export interface DexFeeDisplayInput {
  /** All-in fee in USD (you'll-pay − receive), when the backend computed one. */
  feesTotalUsd?: number
  /** deBridge flat protocol fee in USD, when present. */
  fixFeeUsd?: number
  /**
   * Symbol of the flat fee's NATIVE token (SOL/ETH/POL…), i.e. the field BOTH surfaces gate the
   * `Bridge & swap cost` row on (MED-4). It is read here — by a module that renders nothing —
   * because the wedge arm is only honest when the fee it EXCLUDES can be disclosed beside it, and
   * that disclosure row cannot render without a symbol to denominate it in.
   *
   * `dex.ts:1217` sets it to `srcNativeRow?.symbol ?? null` while `fixFeeNative` (`:1216`) and
   * `fixFeeUsd` (`:1218`) publish from independent lookups, so it is absent on a charged fee whenever
   * the source chain's `supported_tokens` native row is missing or disabled. G4-1: with the row
   * withheld and the wedge rendered, the charged 0.015 SOL / ~$1.14 — 53% of the $2.15 all-in — sat
   * NOWHERE on the last screen before a wallet signature, and the "— paid separately in SOL"
   * annotation died with the row it lives inside. `null` and `undefined` are the same "missing" here.
   */
  fixFeeSymbol?: string | null
  /**
   * True wallet debit in SOURCE-token units — present ONLY when the whole debit is one token
   * (native cross-chain, dex.ts:1255-1257; or same-chain for any source type, dex.ts:1117-1123).
   * Its PRESENCE is the discriminant, not its value. Read it from the SAME payload every other
   * field here comes from (ADDENDUM C, C-1) — never from a sibling prop that can be null while the
   * payload's own field is set.
   */
  youPaySource?: string | null
  /** `isCrossChainFeeDisplayEnabled()`, passed as a PARAMETER — this module must stay import-free. */
  crossChainFeeDisplayEnabled: boolean
}

/** Which arm of ADDENDUM C §7 a payload took. Exposed so tests can assert the BRANCH, not just its effects. */
export type DexFeeDisplayBranch = 'off' | 'singleTokenDebit' | 'stableSplit' | 'legacy'

export interface DexFeeDisplay {
  branch: DexFeeDisplayBranch
  /**
   * The figure to render in the fee-total row, or null → render exactly what today renders.
   * Non-null implies `>= USD_DISPLAY_FLOOR`, so it can never produce a new 'Fee unavailable'.
   *
   * WHEN NON-NULL THE ROW IS WITHHELD, NOT RENAMED (DEV-363 ITEM A, superseding E8's rename). It is
   * no longer the all-in, and a row still called `Total fees` beside a larger USD line item is the
   * contradiction E8's rename tried to fix by relabelling it **`Fees from your receive`** — both
   * surfaces no longer render that label OR any fee-total row at all on this branch. The flat fee
   * keeps its USD magnitude anchor on its own line instead (`— paid separately in {symbol}`, below).
   */
  wedgeUsd: number | null
  /**
   * Annotate the native flat-fee line "— paid separately in {symbol}" (E8). True on exactly the same
   * sub-branch as `wedgeUsd != null`: only there does the fee-total row EXCLUDE this fee, so only
   * there is "paid separately" the truth. On the C-2 fallback the row is the all-in again and the
   * flat fee is inside it, so this stays false.
   */
  paidSeparately: boolean
  /** Annotate the native flat-fee line "— included in You'll pay" (C-8/E7). Never delete that line. */
  includedInYouPay: boolean
}

/** Flag OFF, and every shape F8 does not claim. Frozen so a consumer cannot mutate the shared default. */
const DISPLAY_OFF: DexFeeDisplay = Object.freeze({
  branch: 'off',
  wedgeUsd: null,
  paidSeparately: false,
  includedInYouPay: false,
})

/**
 * #261 F8 — the ONE display-mode decision, shared by the fee card and the review modal so the two
 * cannot state different fees for the same quote. ADDENDUM C §7 is the binding derivation; this is it
 * in code, in the same order, and nothing else may re-derive it.
 *
 * ── THE FOUR ARMS ────────────────────────────────────────────────────────────────────────────────
 *   flag OFF                  -> 'off'. Today's render, byte-identical. CHECKED FIRST (F-D).
 *   youPaySource != null      -> 'singleTokenDebit'. The whole debit is ONE token, so the flat fee is
 *                                already inside `feesTotalUsd` via `youPayUsd` (dex.ts:1258) and the
 *                                breakdown ALREADY reconciles. Total unchanged; the flat-fee line is
 *                                KEPT and annotated (C-8) — deleting it would leave a native user a
 *                                total with ~78% of it unitemised. Rendering a wedge here would
 *                                under-report by the whole flat fee (F-1: $1.88 on base ETH→RH).
 *   feesTotal + fixFee both   -> 'stableSplit' — and, for the WEDGE sub-branch, `fixFeeSymbol` too.
 *                                The debit spans TWO tokens: the source-token input
 *                                and a separate native flat fee. wedge = feesTotalUsd − fixFeeUsd,
 *                                which on this branch is `amountUsd − receiveUsd` exactly
 *                                (dex.ts:1264) — i.e. the part of the cost that comes out of what the
 *                                user RECEIVES, which is the only part `send − receive` can show.
 *                                The fee-total row is WITHHELD (DEV-363 ITEM A) rather than
 *                                relabelled — E8's `Fees from your receive` rename is retired — and
 *                                the native flat fee renders on its own line — native amount AND its
 *                                USD parenthetical — annotated "— paid separately in {symbol}" (E8).
 *                                The stated flat fee and the un-stated wedge still self-reconcile to
 *                                the all-in ($1.01 + $1.14 = $2.15 on rh1); only the wedge figure is
 *                                no longer itself a rendered claim.
 *   otherwise                 -> 'legacy'. 1Click (no `fixFeeUsd` → never a false $0.00), same-chain
 *                                (`feesTotalUsd` null), and any missing-field shape.
 *
 * ── THE FALLBACK IS ONE ARM, NEGATED, AND FAIL-CLOSED (C-2) ──────────────────────────────────────
 * `!(wedge >= USD_DISPLAY_FLOOR)` covers `<= 0`, sub-cent AND `NaN` in one comparison, exactly as
 * `usdRendersAsZero` does. Do NOT "simplify" the negation: the positive form `wedge < FLOOR` is
 * fail-OPEN on NaN and would render `≈ $NaN` as a total on the screen before a wallet signature.
 *
 * ── AND THE WEDGE MAY NOT FIRE WHEN THE FEE IT EXCLUDES CANNOT BE DISCLOSED (G4-1) ───────────────
 * The wedge is a figure that deliberately EXCLUDES the native flat fee, and that is only defensible
 * because the fee is stated on its own line right beside it. That line is gated on `fixFeeSymbol`
 * (MED-4: with no symbol there is no honest denomination, so both surfaces withhold the row rather
 * than print `0.015 USDC` for a 0.015 SOL fee). Those two independently-correct rules COMPOSED into
 * an undisclosed charge: wedge `≈ $1.01` rendered, row withheld, and the charged $1.14 — 53% of the
 * $2.15 all-in — appeared nowhere on the signing screen, with `— paid separately in {symbol}` gone
 * too since it lives inside the withheld row. Flag OFF on the same payload states `≈ $2.15`, which
 * CONTAINS the fee, so the flagged render was strictly LESS disclosing: F-2 ("the native flat fee
 * presented as if outside the total, or its line deleted") verbatim.
 *
 * So the symbol is a PRECONDITION of the wedge, checked here rather than at the two render sites,
 * because the invariant is "no wedge without its excluded-fee line" and one resolver is the only
 * place both surfaces can be held to it at once. Falling back states the all-in, which contains the
 * fee — the safe direction (over-report), and the same C-2 shape a negative wedge already takes.
 *
 * THIS PRECONDITION IS FLAG-ON-ONLY BY CONSTRUCTION, and must stay that way. `!d.crossChainFeeDisplayEnabled`
 * returns DISPLAY_OFF on the first line of the function, so `fixFeeSymbol` is never read in the OFF
 * state and this module cannot alter a flag-OFF render. The CARD's copy of the row gate does carry an
 * explicit `feeDisplayOn` term (`FeeSection/index.tsx`) — not an inconsistency: that row pre-dates F8,
 * so gating it unconditionally would have changed production rendering with the flag off, which is
 * S1's finding F-1 and the reason the term is there. Do not "harmonise" the two by removing it, and
 * do not add a flag term here — the two conditions already agree everywhere both are live.
 *
 * NOT `fixFeeNative`: it is co-assigned with `fixFeeUsd` (`dex.ts:1216` immediately before `:1218`),
 * so `fixFeeUsd != null` ⇒ `fixFeeNative != null` from the only producer — the FE type permits the
 * mirror shape but no backend path emits it.
 *
 * ── WHY THE FALLBACK RETURNS THE **OFF** SHAPE, NOT A HALF-ON ONE ────────────────────────────────
 * ADDENDUM C §7 places the stable-split display state on the whole stable branch. It is scoped HERE
 * to the sub-branch where the wedge actually renders, because a strictly-negative wedge is REACHABLE
 * on a stable route — `feesTotalUsd = Math.max(0, (amountUsd + fixFeeUsd) − receiveUsd)`
 * (dex.ts:1263), so positive slippage clamps it to 0 while `fixFeeUsd` stays ~$1.14. On that arm
 * there is no receive-side wedge to name, so the row must fall all the way back to today's all-in
 * render: a half-on shape would WITHHOLD the fee-total row (DEV-363 ITEM A) for a cost that does not
 * come out of the receive — hiding the whole fee rather than un-naming part of it — and would
 * annotate the flat fee "paid separately" from a total that contains it. `paidSeparately` is scoped
 * identically for exactly that reason.
 *
 * C-7 note: the CTA gate's indifference to the flag no longer rests on this scoping alone. Since
 * MED-7 removed the display parameter from `DexTotalInput`, `dexFeeUnavailable` (BridgePage.tsx:1788)
 * and both display surfaces run the SAME clamp over the SAME payload in either flag state, so no
 * resolver outcome can manufacture a refusal the CTA gate does not share.
 */
export function resolveDexFeeDisplay(d: DexFeeDisplayInput): DexFeeDisplay {
  if (!d.crossChainFeeDisplayEnabled) return DISPLAY_OFF
  if (d.youPaySource != null) {
    return {
      branch: 'singleTokenDebit',
      wedgeUsd: null,
      paidSeparately: false,
      includedInYouPay: true,
    }
  }
  if (d.feesTotalUsd != null && d.fixFeeUsd != null) {
    const wedge = d.feesTotalUsd - d.fixFeeUsd
    // TWO preconditions, ONE fallback site (C-2 is unchanged — still a single `return` out of this
    // arm, still the negated comparison):
    //   · `fixFeeSymbol != null` — the fee this wedge EXCLUDES must be disclosable (G4-1, above).
    //   · `!(wedge >= FLOOR)` — WRITTEN NEGATED DELIBERATELY, fail-CLOSED on NaN. See the header.
    if (d.fixFeeSymbol == null || !(wedge >= USD_DISPLAY_FLOOR)) return { ...DISPLAY_OFF, branch: 'stableSplit' }
    return {
      branch: 'stableSplit',
      wedgeUsd: wedge,
      paidSeparately: true,
      includedInYouPay: false,
    }
  }
  return { ...DISPLAY_OFF, branch: 'legacy' }
}

/**
 * Is the fee estimate in hand the one that was computed for the amount now driving the dest figure?
 * (#336 / R3.) Pure, so the invariant is testable without mounting BridgePage.
 *
 * THE MISMATCH. In DEX **reverse** mode the dest figure is the user's freshly-typed receive target,
 * returned synchronously on every keystroke (`estimatedReceived`, BridgePage.tsx:1536). The fee
 * estimate that supplies `estimatedAllInBps` is keyed on `debouncedDexAmount` (BridgePage.tsx:671) —
 * the SOURCE amount, ~500ms behind, and itself only written after an async reverse quote resolves.
 * Between the keystroke and both of those settling, `dexFee` multiplies a bps belonging to the
 * PREVIOUS amount by the CURRENT target. Typing receive-target 2 then 1000 renders a fee ~20x too
 * high; 1000 then 2 UNDERSTATES it by the same factor — the dangerous direction, on the last screen
 * before a wallet signature.
 *
 * THE TEST (#336 R11 — corrected). `debouncedDexAmount` IS the query key (BridgePage.tsx:671), so
 * `data` is always the entry for the CURRENT key — cached or freshly fetched — and key equality is
 * therefore the exact test. String equality, not numeric, because it is the same string that forms
 * the key ('2.0' and '2' are different keys, hence different estimates).
 *
 * This previously justified itself with "the query carries no `placeholderData`, so react-query
 * blanks `data` on every key change". That is FALSE and worth stating plainly, because a wrong
 * comment on a guard teaches the next reader the wrong invariant. `placeholderData` is indeed absent,
 * but the query sets `staleTime: 30_000` over react-query's default 5-minute `gcTime`
 * (BridgePage.tsx:697; react-query 5.100.9 defaults gcTime to 5 min), so a key the user has already visited is served from cache SYNCHRONOUSLY on
 * the key change with no blank window at all — BridgePage.tsx:2856-2861 already says exactly this
 * about this same query, in the context of the F7 reserve. The predicate is unaffected: a cached
 * entry still belongs to its own key. What DOES follow is a limit worth knowing — a PASSING predicate
 * certifies only that the bps was quoted for this amount, not that it was quoted recently. It can be
 * up to `gcTime` (~5 min) old. Freshness is a separate question, and `dataUpdatedAt` is how the F7
 * reserve asks it.
 *
 * THE FORWARD TWIN (#336 R7). Most of forward mode reads the dest figure out of the same estimate
 * object as the bps (BridgePage.tsx:1540-1542), so the two cannot disagree and a brief debounce lag
 * is normal — those branches stay EXEMPT, and blanket-widening this predicate to all of forward mode
 * would blank the fee card on ordinary typing for no gain. But ONE forward branch does not read from
 * the estimate: `if (!isNativeDest) return bridgeState.amount` (BridgePage.tsx:1546) hands back the
 * source amount, which is the identical stale-bps × fresh-amount product this predicate closed for
 * reverse mode. It is reachable in production: a cross-chain route the provider refused to price
 * emits `estimatedReceived: null` (dex.ts:1276-1278) and omits `estimatedTokenAmount` (dex.ts:1326),
 * so a non-native dest falls straight through to that branch with a live estimate in hand. Hence the
 * second flag — set by the CALLER, which knows which branch produced the figure, so the exemption
 * stays exactly as narrow as the defect.
 *
 * WHAT `amount` IS, EXACTLY (#336 R11 — corrected). It is NOT the raw keystroke. SwapCard debounces
 * its field by 300 ms before dispatching (SwapCard/index.tsx:353-362), so the pipeline is
 *   `localAmount` --300 ms--> `bridgeState.amount` --500 ms--> `debouncedDexAmount`.
 * This predicate compares stages 2 and 3 and is structurally BLIND to stage 1: for the ~300 ms a
 * keystroke spends inside SwapCard, stages 2 and 3 can agree with each other while both disagree with
 * what is on screen, and the predicate returns true. That residual window is named here rather than
 * implied away; closing it needs the field's undebounced value lifted into BridgePage, which is a
 * different change from this one.
 *
 * Fails CLOSED: an unsettled, cleared or unparseable source amount returns false, and the caller
 * renders 'Fee unavailable' rather than a figure nobody quoted.
 */
export function dexEstimateMatchesDestDriver(p: {
  /** True when the dest figure is the user's typed receive target rather than a forward estimate. */
  reverseDrivesDest: boolean
  /**
   * True when the dest figure is the source amount itself (BridgePage.tsx:1546) rather than a field
   * of the estimate. Optional: absent/false keeps the pre-R7 behaviour (forward exempt).
   */
  forwardLiveAmountDrivesDest?: boolean
  /** The source amount as BridgePage holds it (`bridgeState.amount`) — stage 2 of 3, see above. */
  amount: string
  /** The amount the estimate in hand was keyed on (BridgePage `debouncedDexAmount`). */
  estimateAmount: string
}): boolean {
  // Both flagged cases share ONE test, because they are one defect: the figure came from live user
  // input, so it is only multipliable by a bps that was quoted for that same input.
  //
  // THE EXEMPTION STAYS FIRST (#337 T8). This early return sits ABOVE the fail-CLOSED guard that
  // `dexEstimateMatchesAmount` now owns, and the order is load-bearing: an unflagged call with a
  // cleared amount must stay EXEMPT (true), not become a refusal (false). Hoisting the delegation
  // above this line is the natural-looking simplification and it flips that cell — invisibly, because
  // every unflagged case in `dexTotal.test.ts` passes a positive amount. Pinned in
  // `BridgePage.belowMinStaleGate.test.tsx` T8.
  if (!p.reverseDrivesDest && !p.forwardLiveAmountDrivesDest) return true
  return dexEstimateMatchesAmount({ amount: p.amount, estimateAmount: p.estimateAmount })
}

/**
 * Was the estimate in hand computed for THIS amount? (#337.) The core of
 * `dexEstimateMatchesDestDriver` above, lifted out so the SUBMIT GATE can ask the same question the
 * FEE DISPLAY asks — with exactly one comparator in the codebase rather than two free to drift.
 *
 * WHY IT HAD TO BE EXTRACTED RATHER THAN CALLED. `dexEstimateMatchesDestDriver` early-returns `true`
 * when neither driver flag is set, so invoking it from a gate with both flags false is a no-op.
 * Passing `forwardLiveAmountDrivesDest: true` to force the comparison would overload a flag whose
 * documented meaning is "which branch produced the DEST figure" with a second, unrelated meaning —
 * which is precisely how two callers of one predicate drift apart.
 *
 * WHAT IT ANSWERS, AND WHAT IT DOES NOT. It answers "was this estimate keyed on this AMOUNT?" It does
 * NOT answer "…on this ROUTE?" (the #336 R9 route-identity reset, BridgePage.tsx:650-662, owns that),
 * and it does NOT answer "is it FRESH?" — a passing verdict certifies the amount only, and the entry
 * may be up to react-query's `gcTime` (~5 min) old. See the header above for both residuals.
 *
 * STRING EQUALITY, NEVER `parseFloat` (#337 N15). `estimateAmount` IS the react-query key
 * (BridgePage.tsx:674), so '2' and '2.0' are genuinely different cache entries — different estimates.
 * Relaxing this to a numeric compare looks like a false-reject fix and is a SILENT GATE WEAKENING.
 *
 * NAMED RESIDUAL, inherited verbatim from the predicate above: this compares stages 2 and 3 of a
 * three-stage pipeline and is structurally BLIND to stage 1, the ~300 ms a keystroke spends inside
 * SwapCard's own field debounce (SwapCard/index.tsx:350-358). The submit-time half of that residual
 * is closed separately, in `handleReviewConfirm` (BridgePage.tsx, #337 G2), because a click can beat
 * this predicate to the modal.
 *
 * Fails CLOSED: a cleared, zero or unparseable `amount` returns false. A cleared source amount is the
 * in-flight reverse quote (BridgePage.tsx:2516 clears it on every keystroke) — nothing is settled, so
 * there is nothing to certify. NOTE both fields being '' would compare EQUAL, so equality alone is
 * not the test; a settled POSITIVE amount is the precondition.
 */
export function dexEstimateMatchesAmount(p: {
  /** The source amount as BridgePage holds it (`bridgeState.amount`) — stage 2 of 3, see above. */
  amount: string
  /** The amount the estimate in hand was keyed on (BridgePage `debouncedDexAmount`). */
  estimateAmount: string
}): boolean {
  if (!(parseFloat(p.amount || '0') > 0)) return false
  return p.estimateAmount === p.amount
}
